/*
  The Keto Shop: cart rewards (keto-shop/theme/CART-REWARDS.md)

  Takes the place of the theme's "spend £x more for free next day delivery" bar in the cart drawer and on
  /cart (snippets/free-shipping-goal.liquid renders snippets/ks-cart-rewards.liquid instead):
  - First-order visitors (NEW15 on the cart, not a known repeat customer) get a two-step ladder:
    the code's minimum (15% off + free delivery), then free next day delivery.
  - Everyone else gets the free next day goal. Both steps count the goods before any code, as the discount
    app and the checkout rate do.
  - The card is one short line and the bar. From Friday's cutoff to Sunday the fast service is called
    "Express". Optional (setting, off): a line with the next day date (order by 3pm Mon-Fri for dispatch
    today; Friday's next day lands Saturday; after 3pm Friday it's dispatched Monday and lands Tuesday).
  - In the drawer, one line above the checkout button with the same nudge (no pop-up).

  <ks-cart-rewards data-config="{...}"> starts from the cart as Liquid saw it, then follows the theme's
  cart:updated event, which carries the whole cart after every change (the drawer doesn't re-render
  the bar's slot). A discount code that is on the cart but doesn't apply yet (under the minimum) isn't
  visible to Liquid, so the codes come from /cart.js and that event.

  The pure parts (dates, who sees what, the HTML) are exported for tests/test_cart_rewards.py.
*/
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && window.document) {
    window.KsCartRewards = api;
    api.define(window);
  }
})(this, function () {
  'use strict';

  var DAY = 86400000;
  var SHORT_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // ---------------------------------------------------------------- UK calendar

  var ukFormat = null;
  // The UK calendar day (days since 1 Jan 1970) and minutes past midnight for a moment, so every
  // visitor gets the shop's dates whatever their own time zone, either side of the clocks changing.
  function ukWall(ms) {
    try {
      ukFormat = ukFormat || new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London', hourCycle: 'h23',
        year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric'
      });
      var p = {};
      ukFormat.formatToParts(new Date(ms)).forEach(function (x) { p[x.type] = +x.value; });
      return { day: Date.UTC(p.year, p.month - 1, p.day) / DAY, minutes: (p.hour % 24) * 60 + p.minute };
    } catch (e) {
      var d = new Date(ms);
      return { day: Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) / DAY, minutes: d.getHours() * 60 + d.getMinutes() };
    }
  }
  function weekday(day) { return new Date(day * DAY).getUTCDay(); }   // 0 = Sunday
  function isoDay(day) { return new Date(day * DAY).toISOString().slice(0, 10); }

  // "2026-12-25, 2026-12-28" (any separators, notes allowed) -> { <day>: true, ... }
  function parseClosed(text) {
    var out = {};
    String(text || '').split(/[^0-9-]+/).forEach(function (t) {
      var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
      if (m) out[Date.UTC(+m[1], +m[2] - 1, +m[3]) / DAY] = true;
    });
    return out;
  }
  // "15:00", "3pm", "15" -> minutes past midnight (15:00 if unreadable)
  function parseCutoff(text) {
    var m = /^\s*(\d{1,2})(?:[:.](\d{2}))?\s*(am|pm)?\s*$/i.exec(String(text == null ? '' : text));
    if (!m) return 15 * 60;
    var h = +m[1] % 24, mi = Math.min(+(m[2] || 0), 59);
    if (m[3] && /pm/i.test(m[3]) && h < 12) h += 12;
    if (m[3] && /am/i.test(m[3]) && h === 12) h = 0;
    return h * 60 + mi;
  }

  /*
    When a next day order (Royal Mail Tracked 24) placed now would arrive.
    - Leaves us: today if it's Mon-Fri (not a closed date) and before the cutoff, else the next such day.
    - Arrives: the day after it leaves, Saturdays included, Sundays and closed dates skipped. Friday
      before 3pm -> Saturday; Friday after 3pm -> leaves Monday -> Tuesday.
    Returns UK calendar days, and the cutoff moment while today's dispatch is still open.
  */
  function estimate(nowMs, opts) {
    opts = opts || {};
    var closed = opts.closed || {};
    var cutoff = opts.cutoff == null ? 15 * 60 : opts.cutoff;
    function working(d) { var w = weekday(d); return w >= 1 && w <= 5 && !closed[d]; }
    function deliveryDay(d) { var w = weekday(d); return w >= 1 && w <= 6 && !closed[d]; }

    var now = ukWall(nowMs);
    var open = working(now.day) && now.minutes < cutoff;
    var dispatch = now.day, guard = 0;
    if (!open) {
      dispatch++;
      while (!working(dispatch) && guard++ < 60) dispatch++;
    }
    var nextDay = dispatch + 1;
    guard = 0;
    while (!deliveryDay(nextDay) && guard++ < 60) nextDay++;

    return {
      now: nowMs,
      today: now.day,
      minutes: now.minutes,
      dispatch: dispatch,
      nextDay: nextDay,
      // on the minute, so the countdown never runs ahead of the real cutoff
      cutoffMs: open ? nowMs - (nowMs % 60000) + (cutoff - now.minutes) * 60000 : null
    };
  }

  function shortDate(day) {
    var d = new Date(day * DAY);
    return SHORT_DAYS[d.getUTCDay()] + ' ' + d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()];
  }
  // "tomorrow, Tue 13 Oct" / "Sat 17 Oct"
  function dayLabel(day, today) {
    return (day === today + 1 ? 'tomorrow, ' : '') + shortDate(day);
  }
  // "2h 14m" / "45m"; rounded down, never "0m"
  function countdown(ms) {
    var m = Math.max(1, Math.floor(ms / 60000));
    if (m < 60) return m + 'm';
    return Math.floor(m / 60) + 'h' + (m % 60 ? ' ' + (m % 60) + 'm' : '');
  }
  // 900 -> "3pm", 750 -> "12:30pm", 720 -> "midday"
  function clockWords(minutes) {
    var h = Math.floor(minutes / 60) % 24, mi = minutes % 60;
    if (h === 12 && mi === 0) return 'midday';
    return (h % 12 || 12) + (mi ? ':' + (mi < 10 ? '0' : '') + mi : '') + (h < 12 ? 'am' : 'pm');
  }

  // ---------------------------------------------------------------- who sees what

  function money(pence, compact) {
    pence = Math.max(0, Math.round(pence || 0));
    if (compact && pence % 100 === 0) return '£' + pence / 100;
    return '£' + (pence / 100).toFixed(2);
  }

  /*
    cfg:  settings from Liquid (snippets/ks-cart-rewards.liquid): codes, unlock and freeNextDay (pence),
          basis ('subtotal' | 'total'), pct, signedIn, orders, teaserForm, codeSeen
    cart: { spend, codes, codeAmount }  spend = items subtotal before discount codes (what the code's minimum
          and the delivery rates go by); codes = /cart.js discount_codes ([{code, applicable}]) or null when not
          known; codeAmount = pence the first-order code takes off right now
    env:  { returning, klaviyoKnown }  returning = this browser has ordered before (the custom pixel's
          ks_returning cookie); klaviyoKnown = Klaviyo recognises the browser

    audience:
      new15      the code is on the cart and we don't know them as a repeat customer
      returning  the code is on the cart but they are signed in with past orders (it won't apply)
      teaser     no code yet, looks new (signed out, unknown to Klaviyo, no past order on this
                 browser) and a Klaviyo form is set up: invite them to sign up
      standard   everyone else
    tier: 0 working towards the next step, 1 the code's minimum reached, 2 free next day reached
  */
  function model(cfg, cart, env) {
    env = env || {};
    var codes = (cfg.codes || []).map(function (c) { return String(c).toUpperCase(); });
    var spend = Math.max(0, cart.spend || 0);
    var freeNextDay = cfg.freeNextDay || 0;
    var listed = null;
    (cart.codes || []).forEach(function (d) {
      if (d && codes.indexOf(String(d.code).toUpperCase()) !== -1) listed = d;
    });
    var amount = cart.codeAmount || 0;
    var present = !!listed || amount > 0 || !!cfg.codeSeen;
    var signedInRepeat = !!cfg.signedIn && (cfg.orders || 0) > 0;
    var returning = signedInRepeat || !!env.returning;

    // The code's minimum in items-subtotal terms. "Order subtotal over £30" is £30 of goods; "order
    // total over £30" is £30 after the code's own 15%, so about £35.30 of goods.
    var unlockAt = Math.max(0, cfg.unlock || 0);
    if (cfg.basis === 'total' && cfg.pct > 0 && cfg.pct < 100) unlockAt = Math.ceil(unlockAt * 100 / (100 - cfg.pct));

    var audience = 'standard';
    if (present && !returning) audience = 'new15';
    else if (present && signedInRepeat) audience = 'returning';
    else if (!present && cfg.teaserForm && !cfg.signedIn && !returning && !env.klaviyoKnown) audience = 'teaser';

    // Shopify has the last word: if it says the code doesn't apply although the minimum looks met
    // (another rule in the discount app, excluded products...), promise nothing about the code.
    if (audience === 'new15' && listed && listed.applicable === false && spend >= unlockAt) audience = 'standard';

    // Free next day goes by the goods before any code, like the checkout rate: £50 of goods with NEW15 (£42.50
    // to pay) has it. So "Add £x" is in shelf prices and adding exactly that gets there, and any gap between
    // the subtotal and £45 is in the shopper's favour. Counting after the code would ask for money checkout
    // doesn't need, and "add £2.50" would come back as "add 38p" once the 15% came off the new item.
    var tier = spend >= freeNextDay ? 2 : 0;
    if (audience === 'new15' && tier === 0 && spend >= unlockAt) tier = 1;
    var ladder = audience === 'new15' && unlockAt > 0 && unlockAt < freeNextDay;

    return {
      audience: audience,
      tier: tier,
      spend: spend,
      ladder: ladder,
      unlockAt: unlockAt,
      freeNextDay: freeNextDay,
      pctOn: amount > 0,                                  // the 15% is on the cart right now
      toUnlock: Math.max(0, unlockAt - spend),
      toNextDay: Math.max(0, freeNextDay - spend),
      progress: freeNextDay > 0 ? Math.min(1, spend / freeNextDay) : 1,
      marker: ladder ? unlockAt / freeNextDay : null
    };
  }

  // ---------------------------------------------------------------- HTML

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  var ICON_CHECK = '<svg class="ksr__icon ksr__icon--check" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><circle cx="10" cy="10" r="10" fill="currentColor"/><path d="M5.8 10.4l2.7 2.7 5.7-5.9" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_GIFT = '<svg class="ksr__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M3 9h14v8H3zM2 6h16v3H2zM10 6v11M10 6c-1.5-3-5-3-4.3-.6C6.3 6.4 10 6 10 6zm0 0c1.5-3 5-3 4.3-.6C13.7 6.4 10 6 10 6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';
  var ICON_TRUCK = '<svg class="ksr__icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path d="M1.5 5h10v8h-10zM11.5 8h3.6l3.4 3.4V13h-7zM5 16.2a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2zm10 0a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/></svg>';

  function labels(cfg) {
    var discount = cfg.discountLabel || '15% off';
    var delivery = cfg.deliveryLabel || 'free delivery';
    return { discount: discount, delivery: delivery, both: discount + ' + ' + delivery };
  }

  // What the fast service is called. "Next day" from Monday until Friday's cutoff, after 3pm too (with the
  // real date beside it and the cut-off under it). From Friday's cutoff to Sunday midnight, when nothing
  // leaves before Monday, it's the weekend name (setting, "Express"; blank keeps "next day"). The banner
  // and the checkout rate switch on the same rule. Bank holidays follow the day of the week.
  function fastName(cfg, est) {
    var other = String(cfg.expressLabel || '').trim();
    var w = est ? weekday(est.today) : 1;
    var weekend = !!est && (w === 6 || w === 0 || (w === 5 && est.minutes >= (cfg.cutoffMinutes == null ? 900 : cfg.cutoffMinutes)));
    var nextDay = !other || !weekend;
    return {
      title: nextDay ? 'Next day' : other,
      words: nextDay ? 'next day' : other.toLowerCase()
    };
  }

  // The card's one line: the next step, or that free next day is unlocked. Kept short on purpose (more read as
  // clutter in testing); the 15% shows on the cart's own discount line.
  function headline(m, cfg, est) {
    var L = labels(cfg);
    var fast = 'free ' + esc(fastName(cfg, est).words) + ' delivery';
    if (m.tier === 2) return ICON_CHECK + '<span>You’ve unlocked <b>' + fast + '</b></span>';
    if (m.audience === 'new15' && m.tier === 0) {
      return '<span>Add <b>' + money(m.toUnlock) + '</b> for <b>' + esc(m.pctOn ? L.delivery : L.both) + '</b></span>';
    }
    return '<span>Add <b>' + money(m.toNextDay) + '</b> for <b>' + fast + '</b></span>';
  }

  function pct(x) { return (Math.round(x * 1000) / 10) + '%'; }
  // a date that doesn't break across lines
  function dateHtml(day) { return '<span class="ksr__d">' + shortDate(day) + '</span>'; }
  function dayHtml(day, today) { return (day === today + 1 ? 'tomorrow, ' : '') + dateHtml(day); }

  function bar(m, cfg, est, from) {
    var html = '<div class="ksr__track"><div class="ksr__bar" role="progressbar" aria-label="Spend towards free ' + esc(fastName(cfg, est).words) + ' delivery" aria-valuemin="0" aria-valuemax="' +
      (m.freeNextDay / 100) + '" aria-valuenow="' + (Math.min(m.spend, m.freeNextDay) / 100) + '">' +
      '<span class="ksr__fill" style="width:' + pct(from == null ? m.progress : from) + '" data-to="' + pct(m.progress) + '"></span>';
    if (m.marker != null) html += '<span class="ksr__stop' + (m.tier >= 1 ? ' is-done' : '') + '" style="left:' + pct(m.marker) + '">' + ICON_GIFT + '</span>';
    html += '<span class="ksr__stop ksr__stop--end' + (m.tier === 2 ? ' is-done' : '') + '">' + ICON_TRUCK + '</span></div>';
    if (m.marker != null) {
      html += '<div class="ksr__stops" aria-hidden="true">' +
        '<span class="ksr__stop-label' + (m.tier >= 1 ? ' is-done' : '') + '" style="left:' + pct(m.marker) + '">' + money(m.unlockAt, true) + '</span>' +
        '<span class="ksr__stop-label ksr__stop-label--end' + (m.tier === 2 ? ' is-done' : '') + '">' + money(m.freeNextDay, true) + '</span></div>';
    }
    return html + '</div>';
  }

  function cutoffWords(cfg) { return clockWords(cfg.cutoffMinutes == null ? 900 : cfg.cutoffMinutes); }

  // While today's dispatch is open: "Order within 2h 14m for dispatch today" ("Order by 3pm ..." with the
  // countdown off). After the cutoff, at weekends and on closed days just the rule, which explains a next
  // day date that isn't tomorrow. Never "order now and it leaves us tomorrow": that reads as a reason to
  // wait, and ordering now still makes the first dispatch.
  function orderWhen(cfg, est) {
    if (est.cutoffMs == null) return 'Next day cut-off: ' + cutoffWords(cfg) + ', Mon–Fri';
    if (!cfg.countdown) return 'Order by ' + cutoffWords(cfg) + ' for dispatch today';
    return 'Order within <b data-ksr-countdown>' + countdown(est.cutoffMs - est.now) + '</b> for dispatch today';
  }

  // One plain line with the next day date. Not boxes: standard and next day side by side read as options
  // to pick, and the highlighted one as already picked.
  function delivery(m, cfg, est) {
    if (!cfg.dates || !est) return '';
    var fast = fastName(cfg, est);
    return '<p class="ksr__eta">' + ICON_TRUCK + '<span>' + (m.tier === 2 ? 'Free ' + esc(fast.words) : esc(fast.title)) +
      ' delivery: get it <b>' + dayHtml(est.nextDay, est.today) + '</b>. ' + orderWhen(cfg, est) + '.</span></p>';
  }

  function card(m, cfg, est, from) {
    if (m.spend <= 0) return '';
    var L = labels(cfg);
    var html = '<div class="ksr ksr--' + m.audience + ' ksr--tier-' + m.tier + '">' +
      '<p class="ksr__headline' + (m.tier === 2 ? ' is-done' : '') + '">' + headline(m, cfg, est) + '</p>' + bar(m, cfg, est, from);
    html += delivery(m, cfg, est);
    if (m.audience === 'returning' && cfg.returningNote) {
      html += '<p class="ksr__note">' + esc((cfg.codes || [])[0] || 'The code') + ' is for first orders, so it won’t apply to this one.</p>';
    }
    if (m.audience === 'teaser') {
      html += '<div class="ksr__teaser"><span>New here? <b>' + esc(L.both) + '</b> on your first order' +
        (cfg.unlock > 0 ? ' over ' + money(cfg.unlock, true) : '') + '.</span>' +
        '<button type="button" class="ksr__teaser-btn" data-ksr-teaser>Unlock offer</button></div>';
    }
    return html + '<div class="ksr__burst" aria-hidden="true"></div></div>';
  }

  // the one line above the checkout button
  function nudge(m, cfg, est) {
    if (!cfg.nudge || m.spend <= 0) return '';
    var L = labels(cfg);
    var dates = !!(est && cfg.dates);
    var fast = esc(fastName(cfg, est).words);
    if (m.tier === 2) {
      if (!dates) return '';
      return '<p class="ksr-nudge is-done">' + ICON_CHECK + '<span>Free ' + fast + ' delivery: get it <b>' + dayHtml(est.nextDay, est.today) + '</b></span></p>';
    }
    if (m.audience === 'new15' && m.tier === 0) {
      if (m.toUnlock > cfg.nudgeGap) return '';
      return '<p class="ksr-nudge">' + ICON_GIFT + '<span>Add <b>' + money(m.toUnlock) + '</b> to unlock <b>' + esc(m.pctOn ? L.delivery : L.both) + '</b></span></p>';
    }
    if (m.toNextDay > cfg.nudgeGap) return '';
    if (!dates) return '<p class="ksr-nudge">' + ICON_TRUCK + '<span>Add <b>' + money(m.toNextDay) + '</b> for <b>free ' + fast + ' delivery</b></span></p>';
    return '<p class="ksr-nudge">' + ICON_TRUCK + '<span>Add <b>' + money(m.toNextDay) + '</b> for free ' + fast + ' delivery: get it <b>' +
      dayHtml(est.nextDay, est.today) + '</b></span></p>';
  }

  // ---------------------------------------------------------------- browser

  function readCookie(name) {
    try {
      var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  // Klaviyo recognises this browser (an email click or a form sign-up); same test as the offer bar
  function knownToKlaviyo() {
    try {
      var raw = readCookie('__kla_id');
      if (!raw) return false;
      var data = JSON.parse(atob(raw));
      return !!(data && (data.$exchange_id || data.$email));
    } catch (e) { return false; }
  }
  function codeAmountFrom(cartJson, codes) {
    var total = 0;
    (cartJson.cart_level_discount_applications || []).forEach(function (a) {
      if (codes.indexOf(String(a.title || '').toUpperCase()) !== -1) total += a.total_allocated_amount || 0;
    });
    return total;
  }

  function define(win) {
    if (!win.customElements || win.customElements.get('ks-cart-rewards')) return;
    var doc = win.document;
    var latest = null;       // the newest cart JSON (cart:updated or /cart.js)
    var tick = 0;            // orders cart updates against elements: newer data wins
    var latestTick = 0;
    var loading = null;
    var lastProgress = null; // so a re-created bar grows from where it was
    var live = [];

    // Keep the stylesheet in <head>: the cart page re-renders the snippet (and its <link>) on every
    // change, and a fresh <link> would leave the card unstyled for a moment.
    try {
      var sheet = doc.querySelector('link[href*="ks-cart-rewards.css"]');
      if (sheet && !doc.querySelector('link[data-ksr-css]')) {
        var keep = doc.createElement('link');
        keep.rel = 'stylesheet';
        keep.href = sheet.href;
        keep.setAttribute('data-ksr-css', '');
        doc.head.appendChild(keep);
      }
    } catch (e) {}

    function session(key, value) {
      try {
        if (value === undefined) return win.sessionStorage.getItem(key);
        win.sessionStorage.setItem(key, value);
      } catch (e) { return null; }
    }
    function track(name, params) {
      try {
        win.dataLayer = win.dataLayer || [];
        // queued the way gtag.js reads it, so GA4 picks it up whenever it loads
        (function () { win.dataLayer.push(arguments); })('event', name, params);
      } catch (e) {}
    }
    function fetchCart() {
      if (loading) return loading;
      var base = (win.Shopify && win.Shopify.routes && win.Shopify.routes.root) || '/';
      loading = win.fetch(base + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (j) { if (j) { latest = j; latestTick = ++tick; } loading = null; return j; });
      return loading;
    }
    // run cb once the element is on screen (the drawer may still be sliding in), or give up
    function whenVisible(el, cb) {
      if (!('IntersectionObserver' in win)) return cb();
      var done = false;
      var io = new win.IntersectionObserver(function (entries) {
        if (done || !entries.some(function (e) { return e.isIntersecting; })) return;
        done = true;
        io.disconnect();
        setTimeout(cb, 250);
      }, { threshold: 0.6 });
      io.observe(el);
      setTimeout(function () { if (!done) { done = true; io.disconnect(); } }, 4000);
    }

    doc.addEventListener('cart:updated', function (e) {
      var c = e && e.detail && e.detail.cart;
      if (c && c.items_subtotal_price != null) {
        latest = c;
        latestTick = ++tick;
        live.slice().forEach(function (el) { el.render(); });
      }
    });

    class KsCartRewardsElement extends win.HTMLElement {
      connectedCallback() {
        try { this.cfg = JSON.parse(this.getAttribute('data-config') || 'null'); } catch (e) { this.cfg = null; }
        if (!this.cfg) return;
        var cfg = this.cfg;
        cfg.codes = (cfg.codes || []).map(function (c) { return String(c).toUpperCase(); });
        cfg.closedSet = parseClosed(cfg.closed);
        cfg.cutoffMinutes = parseCutoff(cfg.cutoff);
        this.born = ++tick;
        if (live.indexOf(this) === -1) live.push(this);
        if (!this.clickBound) {
          this.clickBound = true;
          this.addEventListener('click', (e) => {
            if (e.target && e.target.closest && e.target.closest('[data-ksr-teaser]')) this.openTeaser();
          });
        }
        // Liquid only sees the code once it applies; under the minimum it's only in /cart.js. Until
        // that answer is in, a cart that might hold the code stays blank rather than wrong.
        if (!latest && !cfg.codeSeen && cfg.spend > 0) {
          this.innerHTML = '';
          fetchCart().then(() => { if (this.isConnected) this.render(); });
          setTimeout(() => { if (this.isConnected && !this.rendered) this.render(); }, 1500);
        } else {
          this.render();
        }
      }

      disconnectedCallback() {
        clearTimeout(this.timer);
        var i = live.indexOf(this);
        if (i !== -1) live.splice(i, 1);
        if (this.nudgeEl && this.nudgeEl.parentNode) this.nudgeEl.parentNode.removeChild(this.nudgeEl);
      }

      render() {
        var cfg = this.cfg;
        clearTimeout(this.timer);
        if (!cfg) return;
        this.rendered = true;
        if (cfg.currency !== 'GBP') { this.innerHTML = ''; return; }

        // the newest cart wins: Liquid's when this element is newer, else the last cart:updated or
        // /cart.js (which also lists a code that doesn't apply yet)
        var cart = { spend: cfg.spend, codes: null, codeAmount: cfg.codeAmount || 0 };
        if (latest && latestTick > this.born) {
          cart = { spend: latest.items_subtotal_price, codes: latest.discount_codes || null, codeAmount: codeAmountFrom(latest, cfg.codes) };
        } else if (latest) {
          cart.codes = latest.discount_codes || null;
        }
        var m = model(cfg, cart, { returning: readCookie('ks_returning') === '1', klaviyoKnown: knownToKlaviyo() });
        // always worked out: even with the dates hidden, the fast service's name follows them
        var est = estimate(Date.now(), { cutoff: cfg.cutoffMinutes, closed: cfg.closedSet });

        var html = card(m, cfg, est, lastProgress);
        if (html !== this.lastHtml) {   // the clock ticks redraw only when the words change
          this.lastHtml = html;
          this.innerHTML = html;
          var fill = this.querySelector('.ksr__fill');
          if (fill && fill.style.width !== fill.getAttribute('data-to')) {
            win.requestAnimationFrame(function () {
              win.requestAnimationFrame(function () { fill.style.width = fill.getAttribute('data-to'); });
            });
          }
          this.celebrate(m);
        }
        lastProgress = m.progress;
        this.placeNudge(nudge(m, cfg, est));

        // the countdown moves on the minute and gives way to the cut-off at 3pm (and "Express" on Fridays);
        // after that, check again every few minutes (back to "next day" at Monday midnight)
        var wait = est.cutoffMs != null ? Math.min(est.cutoffMs - est.now, 60000 - (est.now % 60000)) + 50 : 300000;
        this.timer = setTimeout(() => { if (this.isConnected) this.render(); }, Math.max(1000, wait));
      }

      // In the drawer, the line above the checkout button. The drawer replaces its footer on every cart
      // change and then fires cart:updated, which re-renders this, so the line is put back each time.
      placeNudge(html) {
        var drawer = this.closest('cart-drawer');
        var button = drawer && drawer.querySelector('[name="checkout"]');
        var anchor = button && (button.closest('form') || button.parentElement);
        if (!anchor || !anchor.parentNode) return;
        if (!this.nudgeEl || this.nudgeEl.nextSibling !== anchor) {
          if (this.nudgeEl && this.nudgeEl.parentNode) this.nudgeEl.parentNode.removeChild(this.nudgeEl);
          this.nudgeEl = doc.createElement('div');
          this.nudgeEl.className = 'ksr-nudge-slot';
          anchor.parentNode.insertBefore(this.nudgeEl, anchor);
        }
        if (this.nudgeEl.innerHTML !== html) this.nudgeEl.innerHTML = html;
        if (this.style.getPropertyValue('--ksr-accent')) this.nudgeEl.style.setProperty('--ksr-accent', this.style.getPropertyValue('--ksr-accent'));
      }

      // Crossing a step: a GA4 event, a pulse and confetti (not with reduced motion). The step is kept
      // for the browser session (0 below both, 1 the code's minimum, 2 free next day), so it fires
      // once per crossing, including when the pop-up adds the code to a cart already over £30. The
      // first render of a session only sets the starting point: a cart that was already past a step
      // doesn't celebrate on page load.
      celebrate(m) {
        var before = session('ks_cart_tier');
        if (before === String(m.tier)) return;
        session('ks_cart_tier', String(m.tier));
        if (before === null || !(m.tier > +before)) return;
        track('cart_reward_unlocked', {
          reward: m.tier === 1 ? 'first_order_code' : 'free_next_day', audience: m.audience, value: m.spend / 100
        });
        var box = this.querySelector('.ksr');
        if (!box) return;
        var still = win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
        whenVisible(box, function () {
          box.classList.add('is-celebrating');
          var burst = box.querySelector('.ksr__burst');
          if (!still && burst) {
            var colours = ['#1a401c', '#3f8f45', '#f2b705', '#c4301c', '#7cc47f'];
            var html = '';
            for (var i = 0; i < 28; i++) {
              html += '<i style="left:' + Math.round(Math.random() * 100) + '%;background:' + colours[i % colours.length] +
                ';--dx:' + Math.round(Math.random() * 120 - 60) + 'px;--r:' + Math.round(Math.random() * 720 - 360) +
                'deg;animation-delay:' + (i % 7) * 35 + 'ms"></i>';
            }
            burst.innerHTML = html;
          }
          setTimeout(function () { box.classList.remove('is-celebrating'); if (burst) burst.innerHTML = ''; }, 1900);
        });
      }

      // "Unlock offer": open the Klaviyo sign-up form (it applies the code), then refresh the drawer
      openTeaser() {
        var id = this.cfg && this.cfg.teaserForm;
        if (!id) return;
        win._klOnsite = win._klOnsite || [];
        win._klOnsite.push(['openForm', id]);
        track('cart_reward_teaser_click', { form: id });
        if (win.__ksrTeaserListening) return;
        win.__ksrTeaserListening = true;
        win.addEventListener('klaviyoForms', function (e) {
          if (!e.detail || e.detail.formId !== id || e.detail.type !== 'submit') return;
          setTimeout(function () {
            latest = null;
            doc.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: false } }));
          }, 1500);
        });
      }
    }

    win.customElements.define('ks-cart-rewards', KsCartRewardsElement);
  }

  return {
    ukWall: ukWall, isoDay: isoDay, parseClosed: parseClosed, parseCutoff: parseCutoff, estimate: estimate,
    shortDate: shortDate, dayLabel: dayLabel, countdown: countdown, clockWords: clockWords,
    money: money, model: model, fastName: fastName, headline: headline, card: card, nudge: nudge, define: define
  };
});
